import { KoobTooltip } from '@koob/ui';
import { useTranslation } from 'react-i18next';
import {
  useTripFilters,
  useTripStore,
  useTripTotal,
} from '@koob/trip-designer';
import { formatCurrency } from '../../utils';

// Same kicker as KoobStat, the TD header's typography atom.
const kicker =
  'text-[9px] font-bold uppercase leading-tight tracking-widest text-muted-foreground';

// KD-3970: read-only mirror of the Trip Designer header, shown in the app
// header outside the TD. Reads the persisted trip store, so it follows
// unsaved changes too. The whole pill resumes the trip; × closes it.
export default function KoobNavbarCurrentTrip({
  showPrice,
  onResume,
  onClose,
}) {
  const { t } = useTranslation('tripDesigner');
  const tripFilters = useTripFilters();
  const trip = useTripStore(state => state.trip);
  // No "calculating" state here (KD-3970 AC4): the pill shows the total as it
  // stands, priceLoading is ignored.
  const { globalTripPrice, globalTripCurrency, participants } = useTripTotal();

  const tripName = tripFilters?.programName;
  const version = trip?.currentVersion?.version;
  const mismatch = globalTripCurrency === 'mismatch';
  const hasPrice = !mismatch && globalTripCurrency != null;

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onResume}
        data-testid="navbar-current-trip"
        className="group flex h-[42px] cursor-pointer items-stretch divide-x divide-y-0 divide-solid divide-border rounded-lg border border-solid border-border bg-white p-0 text-left transition-shadow hover:border-brand hover:ring-[3px] hover:ring-brand/15 focus-visible:border-brand focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand/15"
      >
        <span className="flex min-w-0 flex-col justify-center px-3">
          <span className={kicker}>{t('navbar.currentTrip')}</span>
          <span
            title={tripName}
            className="max-w-[180px] truncate whitespace-nowrap text-[13px] font-bold leading-tight text-brand"
          >
            {tripName}
          </span>
        </span>

        {version != null && (
          <span className="flex items-center gap-2 whitespace-nowrap px-3 text-[13px] font-medium text-foreground">
            <i className="fa-regular fa-code-branch text-foreground" />
            {t('compose.versions.version')} {version}
          </span>
        )}

        {showPrice && mismatch && (
          <KoobTooltip
            tip={
              // 280px wide with the tooltip's 12px side padding.
              <span className="block w-64 py-0.5 text-left text-[13px] font-normal leading-[1.4]">
                {t('navbar.currencyMismatchTip')}
              </span>
            }
            align="start"
            maxWidth={280}
            className="items-center gap-2 whitespace-nowrap px-3"
          >
            <i className="fa-solid fa-circle-exclamation text-[15px] text-red-600" />
            <span className="text-[13px] font-semibold text-red-600">
              {t('misMatchCurrency')}
            </span>
          </KoobTooltip>
        )}

        {showPrice && hasPrice && (
          <span className="flex items-center gap-2.5 whitespace-nowrap px-3">
            <span className={kicker}>{t('from')}</span>
            {/* id kept from the legacy navbar block — e2e hooks onto it */}
            <span
              id="navbar-trip-designer-price"
              className="flex flex-col leading-[1.2]"
            >
              <span className="text-[13px] font-semibold text-brand">
                {formatCurrency({
                  amount: globalTripPrice,
                  currency: globalTripCurrency,
                })}
              </span>
              {participants > 0 && (
                <span className="text-[11px] text-muted-foreground">
                  {formatCurrency({
                    amount: globalTripPrice / participants,
                    currency: globalTripCurrency,
                  })}{' '}
                  / {t('pax')}
                </span>
              )}
            </span>
          </span>
        )}

        <span className="flex items-center gap-2 whitespace-nowrap rounded-r-[7px] px-3.5 text-[13px] font-semibold text-k-h transition-colors group-hover:bg-brand/[.08] group-hover:text-brand group-focus-visible:bg-brand/[.08] group-focus-visible:text-brand">
          <i className="fa-regular fa-pen-ruler text-brand" />
          {t('navbar.resume')}
          <i className="fa-regular fa-arrow-right text-[11px]" />
        </span>
      </button>

      <KoobTooltip tip={t('navbar.closeTrip')}>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('navbar.closeTrip')}
          data-testid="navbar-close-trip"
          className="flex size-9 cursor-pointer items-center justify-center rounded-md border border-solid border-border bg-white text-muted-foreground transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <i className="fa-regular fa-xmark" />
        </button>
      </KoobTooltip>
    </div>
  );
}
